import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebounced } from '@/components/parameters/useDebounced';
import { useApi } from '@/api/client';
import { Pager } from '@/components/Pager';
import type { ReportPackDeliveryMode, ReportPackSummary } from '@/types';

/** Matches My Subscriptions and the KT Insights history, so every list here pages alike. */
const pageSize = 50;

/** Short enough for a table cell; the editor spells the same choice out in full. */
function deliveryLabel(mode: ReportPackDeliveryMode): string {
  return mode === 'PickupAndEmail' ? 'Pickup + email' : 'Pickup only';
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function ReportPacks() {
  const api = useApi();
  const [params, setParams] = useSearchParams();

  // Same split as My Subscriptions: the filter is in the URL because a filtered view is worth
  // pasting to a colleague and survives the trip into a pack and back; the offset is not, because
  // "page 2" of a list that has moved on means nothing to whoever opens the link.
  const [skip, setSkip] = useState(0);

  const search = params.get('q') ?? '';
  const [searchInput, setSearchInput] = useState(search);
  const settledSearch = useDebounced(searchInput, 250);

  useEffect(() => {
    // Guarded on inequality so a mount with nothing typed does not push a router navigation that
    // changes nothing.
    if (settledSearch === search) return;
    const next = new URLSearchParams(params);
    if (settledSearch) next.set('q', settledSearch); else next.delete('q');
    setParams(next, { replace: true });
    // Pushes the local value OUT to the URL. Reacting to the URL here as well would make the two
    // chase each other on every write — see the same effect on My Subscriptions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledSearch]);

  // The other direction, for what the box cannot cause: a pasted link or the back button.
  useEffect(() => {
    setSearchInput(current => (current.trim().toLowerCase() === search.trim().toLowerCase() ? current : search));
  }, [search]);

  useEffect(() => setSkip(0), [settledSearch]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['packs'],
    queryFn: api.listPacks,
    placeholderData: keepPreviousData
  });

  if (isLoading) return <div className="placeholder">Loading report packs...</div>;
  if (error) return <div className="error-banner">Could not load report packs: {(error as Error).message}</div>;

  const all = data ?? [];

  /*
   * Filtered and paged in the browser, unlike every other list here — deliberately, and only
   * because GET /admin/packs answers with the whole array and no total. There are a handful of
   * packs, not 19,937 execution-log rows, so the page holds all of them and `total` below is the
   * real total rather than a count of what happens to be loaded.
   */
  const needle = settledSearch.trim().toLowerCase();
  const matching = needle
    ? all.filter(p =>
      p.name.toLowerCase().includes(needle)
      || (p.description ?? '').toLowerCase().includes(needle))
    : all;
  const rows = matching.slice(skip, skip + pageSize);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Report packs</h1>
          <div className="sub">
            A standard suite of monthly reports, applied to a customer in one pass. Each item becomes
            an ordinary subscription you can manage from the usual screens afterwards.
          </div>
        </div>
        <Link to="/admin/packs/new" className="btn-primary">New pack</Link>
      </div>

      <div className="list-filters">
        <input
          className="input"
          style={{ maxWidth: 360 }}
          type="search"
          placeholder="Search packs by name or description..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
      </div>

      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Pack</th>
              <th>Reports</th>
              {/* Answerable per row only because a pack has ONE schedule. While the recurrence
                  lived on each item there was no single sentence to put here that was not a lie
                  about at least one of them. */}
              <th>Schedule</th>
              <th>Delivery</th>
              <th>Customers</th>
              <th>Status</th>
              <th>Last applied</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="placeholder">
                  {all.length === 0
                    ? 'No packs yet. Create one to define a standard suite of reports.'
                    : `No pack matches "${settledSearch}".`}
                </td>
              </tr>
            ) : rows.map(pack => <Row key={pack.id} pack={pack} />)}
          </tbody>
        </table>
      </div>

      <Pager
        skip={skip}
        take={pageSize}
        shown={rows.length}
        total={matching.length}
        busy={isFetching}
        onChange={setSkip}
      />
    </>
  );
}

function Row({ pack }: { pack: ReportPackSummary }) {
  const navigate = useNavigate();
  return (
    <tr onClick={() => navigate(`/admin/packs/${pack.id}`)}>
      <td data-label="">
        <div className="report-name">{pack.name}</div>
        {pack.description && (
          <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-500)', marginTop: 2 }}>
            {pack.description}
          </div>
        )}
      </td>
      <td data-label="Reports">{pack.itemCount}</td>
      <td data-label="Schedule">{pack.scheduleDescription || '—'}</td>
      <td data-label="Delivery">{deliveryLabel(pack.deliveryMode)}</td>
      <td data-label="Customers">{pack.customerCount}</td>
      <td data-label="Status"><PackStatus pack={pack} /></td>
      <td data-label="Last applied">{formatDate(pack.lastAppliedUtc)}</td>
    </tr>
  );
}

/**
 * What the list can honestly say about a pack.
 *
 * Not health. Whether a customer's subscriptions still match the pack is a per-placement answer
 * that only /admin/packs/{id}/enrolments carries, and asking it once per row would be one round
 * trip per pack to render a column — so this column states the pack's own state instead, and the
 * pack page is where "4 need attention" is answered. A green "all in sync" here would be a claim
 * this screen has no data for.
 */
function PackStatus({ pack }: { pack: ReportPackSummary }) {
  if (!pack.isEnabled) return <span className="pill pill-unknown">Disabled</span>;
  if (pack.customerCount === 0) return <span className="pill pill-unknown">Draft</span>;
  return <span className="pill pill-success">In use</span>;
}
